import React from 'react';
import { authService } from '../../services/authService';
import { Shield, Users, MessageSquare, Activity, LogOut, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AdminLayoutProps {
    activeTab: string;
    onTabChange: (tab: string) => void;
    children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ activeTab, onTabChange, children }) => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const isAdmin = authService.isAdmin();

    if (!authService.isAuthenticated()) {
        navigate('/login');
        return null;
    }

    if (!isAdmin) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-gray-100">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center">
                    <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-6">You do not have permission to access the admin area.</p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                    >
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const navItems = [
        { id: 'users', label: 'User Management', icon: Users },
        { id: 'messaging', label: 'Messages', icon: MessageSquare },
        { id: 'audit', label: 'Audit Logs', icon: Activity },
    ];

    return (
        <div className="h-screen flex bg-gray-50">
            {/* Admin Sidebar */}
            <div className="w-64 bg-slate-900 text-slate-300 flex flex-col">
                <div className="p-4 border-b border-slate-700 flex items-center gap-2">
                    <Shield className="text-red-500" />
                    <span className="font-bold text-white tracking-wider">SECURE ADMIN</span>
                </div>

                <div className="flex-1 py-4">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => onTabChange(item.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${activeTab === item.id
                                    ? 'bg-slate-800 text-white border-r-4 border-red-500'
                                    : 'hover:bg-slate-800 hover:text-white'
                                }`}
                        >
                            <item.icon size={18} />
                            <span className="font-medium">{item.label}</span>
                        </button>
                    ))}
                </div>

                <div className="p-4 border-t border-slate-800">
                    <div className="flex items-center gap-3 px-4 py-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                            <Lock size={14} className="text-green-400" />
                        </div>
                        <div className="text-xs">
                            <div className="text-white font-medium">{user?.name}</div>
                            <div className="text-slate-500">Security Clearance: L5</div>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-400 hover:text-white"
                    >
                        <LogOut size={16} />
                        Return to App
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-auto">
                <div className="p-8">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default AdminLayout;
